import { applyCanvasReferenceCommandV3 } from "@seedance/contracts";
import type { CanvasDocumentV3 as CanvasDocument } from "@seedance/contracts";
import {
  copyCanvasSelection,
  pasteCanvasSelection,
  type CanvasClipboard
} from "../clipboard.js";
import {
  applyCanvasCommand,
  type CanvasHistoryState
} from "../model/history.js";

export type Alignment = "left" | "center" | "right" | "top" | "middle" | "bottom";
export type DistributionAxis = "horizontal" | "vertical";

const duplicateOffset = 32;
export const collapsedNodeHeight = 52;

export { copyCanvasSelection, pasteCanvasSelection, type CanvasClipboard };

export function applyCanvasHistoryFrame(
  history: CanvasHistoryState,
  frame: { type: "dragging" | "viewport" | "complete"; document: CanvasDocument }
): CanvasHistoryState {
  if (frame.type === "dragging" || frame.type === "viewport") return history;
  return applyCanvasCommand(history, { type: "replace_document", document: frame.document });
}

export function collapsedNodeProjection(node: CanvasDocument["nodes"][number]): {
  className?: string;
  height?: number;
} {
  return node.collapsed
    ? { className: "studio-flow-node--collapsed", height: collapsedNodeHeight }
    : {};
}

function selectedNodes(document: CanvasDocument, selectedIds: readonly string[]) {
  const selected = new Set(selectedIds);
  return document.nodes.filter((node) => selected.has(node.id));
}

function positionValue(
  node: CanvasDocument["nodes"][number],
  alignment: Alignment
): number {
  switch (alignment) {
    case "right": return node.position.x + node.size.width;
    case "center": return node.position.x + node.size.width / 2;
    case "bottom": return node.position.y + node.size.height;
    case "middle": return node.position.y + node.size.height / 2;
    case "top": return node.position.y;
    case "left": return node.position.x;
  }
}

function hash(value: string, seed: number): string {
  let result = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    result = Math.imul(result ^ value.charCodeAt(index), 0x01000193) >>> 0;
  }
  return result.toString(16).padStart(8, "0");
}

function deterministicId(namespace: string, values: readonly string[], occupied: Set<string>): string {
  for (let attempt = 0; ; attempt += 1) {
    const source = `${namespace}:${values.join(",")}:${attempt}`;
    const hexadecimal = `${hash(source, 0x811c9dc5)}${hash(source, 0x9e3779b9)}${hash(source, 0x85ebca6b)}${hash(source, 0xc2b2ae35)}`;
    const id = `${hexadecimal.slice(0, 8)}-${hexadecimal.slice(8, 12)}-4${hexadecimal.slice(13, 16)}-8${hexadecimal.slice(17, 20)}-${hexadecimal.slice(20, 32)}`;
    if (!occupied.has(id)) {
      occupied.add(id);
      return id;
    }
  }
}

export function alignNodes(
  document: CanvasDocument,
  selectedIds: readonly string[],
  alignment: Alignment
): CanvasDocument {
  const nodes = selectedNodes(document, selectedIds);
  if (nodes.length < 2) return document;
  const values = nodes.map((node) => positionValue(node, alignment));
  const target = alignment === "left" || alignment === "top"
    ? Math.min(...values)
    : alignment === "right" || alignment === "bottom"
      ? Math.max(...values)
      : values.reduce((sum, value) => sum + value, 0) / values.length;
  const selected = new Set(nodes.map(({ id }) => id));
  return {
    ...document,
    nodes: document.nodes.map((node) => {
      if (!selected.has(node.id)) return node;
      const difference = target - positionValue(node, alignment);
      return alignment === "left" || alignment === "center" || alignment === "right"
        ? { ...node, position: { ...node.position, x: node.position.x + difference } }
        : { ...node, position: { ...node.position, y: node.position.y + difference } };
    })
  };
}

export function distributeNodes(
  document: CanvasDocument,
  selectedIds: readonly string[],
  axis: DistributionAxis
): CanvasDocument {
  const nodes = selectedNodes(document, selectedIds);
  if (nodes.length < 3) return document;
  const sorted = [...nodes].sort((left, right) => {
    const difference = axis === "horizontal"
      ? left.position.x - right.position.x
      : left.position.y - right.position.y;
    return difference || left.id.localeCompare(right.id);
  });
  const first = sorted[0]!;
  const last = sorted.at(-1)!;
  const span = axis === "horizontal"
    ? last.position.x - first.position.x
    : last.position.y - first.position.y;
  const positions = new Map(
    sorted.map((node, index) => [
      node.id,
      (axis === "horizontal" ? first.position.x : first.position.y) + span * index / (sorted.length - 1)
    ] as const)
  );
  return {
    ...document,
    nodes: document.nodes.map((node) => {
      const position = positions.get(node.id);
      if (position === undefined) return node;
      return axis === "horizontal"
        ? { ...node, position: { ...node.position, x: position } }
        : { ...node, position: { ...node.position, y: position } };
    })
  };
}

export function groupNodes(document: CanvasDocument, selectedIds: readonly string[]): CanvasDocument {
  const nodes = selectedNodes(document, selectedIds);
  if (nodes.length === 0) return document;
  const occupied = new Set([...document.nodes.map(({ id }) => id), ...document.edges.map(({ id }) => id), ...document.groups.map(({ id }) => id)]);
  return {
    ...document,
    groups: [...document.groups, {
      id: deterministicId("group", nodes.map(({ id }) => id), occupied),
      label: `分组 ${document.groups.length + 1}`,
      nodeIds: nodes.map(({ id }) => id),
      color: "#7462ef"
    }]
  };
}

export function ungroupNodes(document: CanvasDocument, groupIds: readonly string[]): CanvasDocument {
  const selected = new Set(groupIds);
  const groups = document.groups.filter((group) => !selected.has(group.id));
  return groups.length === document.groups.length ? document : { ...document, groups };
}

export function moveGroupNodes(
  document: CanvasDocument,
  groupId: string,
  delta: { x: number; y: number }
): CanvasDocument {
  if (!Number.isFinite(delta.x) || !Number.isFinite(delta.y) || (delta.x === 0 && delta.y === 0)) {
    return document;
  }
  const group = document.groups.find(({ id }) => id === groupId);
  if (!group) return document;
  const members = new Set(group.nodeIds);
  if (!document.nodes.some(({ id }) => members.has(id))) return document;
  return {
    ...document,
    nodes: document.nodes.map((node) => members.has(node.id)
      ? {
          ...node,
          position: {
            x: node.position.x + delta.x,
            y: node.position.y + delta.y
          }
        }
      : node)
  };
}

export function collapseNodes(document: CanvasDocument, selectedIds: readonly string[]): CanvasDocument {
  const selected = new Set(selectedIds);
  if (!document.nodes.some((node) => selected.has(node.id))) return document;
  return {
    ...document,
    nodes: document.nodes.map((node) => selected.has(node.id)
      ? { ...node, collapsed: !node.collapsed }
      : node)
  };
}

export function duplicateNodes(document: CanvasDocument, selectedIds: readonly string[]): CanvasDocument {
  const nodes = selectedNodes(document,selectedIds);
  if (!nodes.length) return document;
  const origin = {x:Math.min(...nodes.map(node=>node.position.x))+duplicateOffset,y:Math.min(...nodes.map(node=>node.position.y))+duplicateOffset};
  return pasteCanvasSelection(document,copyCanvasSelection(document,selectedIds),origin);
}

export function deleteNodes(document: CanvasDocument, selectedIds: readonly string[]): CanvasDocument {
  return applyCanvasReferenceCommandV3(document,{type:"delete_nodes",nodeIds:selectedIds});
}
